import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    EyeIcon,
    PencilIcon,
    TrashIcon,
    ChevronDownIcon,
    ChevronUpIcon,
} from '@heroicons/react/24/outline';
import moment from 'moment';

// Mobile Orders List Component
const MobileOrdersList = ({
                              orders,
                              getStatusBadgeColor,
                              handleDeleteClick,
                              hasRole,
                              expandedRows,
                              toggleRowExpansion
                          }) => {
    return (
        <div className="md:hidden space-y-4">
            {orders.length > 0 ? (
                orders.map((order) => (
                    <div key={order.id} className="bg-white shadow rounded-lg overflow-hidden">
                        <div
                            className="p-4 cursor-pointer"
                            onClick={() => toggleRowExpansion(order.id)}
                        >
                            <div className="flex justify-between items-start">
                                <div className="space-y-1">
                                    <div className="flex items-center">
                                        <span className="text-primary-600 font-medium">#{order.order_number}</span>
                                        {order.shopify_id && (
                                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                                Shopify
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <span className="text-sm text-gray-500">
                                            {moment(order.order_date).format('MMM D, YYYY h:mm A')}
                                        </span>
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeColor(order.status)}`}>
                                            {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                                        </span>
                                    </div>
                                    <div className="text-sm text-gray-700">{order.customer_name || 'Unknown Customer'}</div>
                                    <div className="text-xs text-gray-500">
                                        {order.items && order.items.length > 0 && (
                                            <div className="flex flex-wrap gap-1">
                                                {order.items.slice(0, 2).map((item, idx) => (
                                                    <span key={idx} className="truncate max-w-[150px]">{item.sku || 'No SKU'}</span>
                                                ))}
                                                {order.items.length > 2 && (
                                                    <span className="text-gray-400">+{order.items.length - 2} more</span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center">
                                    <div className="flex space-x-2 mr-2">
                                        <Link
                                            to={`/orders/${order.id}`}
                                            className="text-primary-600 p-1 rounded-full hover:bg-primary-100"
                                            title="View"
                                        >
                                            <EyeIcon className="h-5 w-5" />
                                        </Link>

                                        {hasRole('admin') && (
                                            <>
                                                <Link
                                                    to={`/orders/${order.id}/edit`}
                                                    className="text-indigo-600 p-1 rounded-full hover:bg-indigo-100"
                                                    title="Edit"
                                                >
                                                    <PencilIcon className="h-5 w-5" />
                                                </Link>

                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteClick(order);
                                                    }}
                                                    className="text-red-600 p-1 rounded-full hover:bg-red-100"
                                                    title="Delete"
                                                >
                                                    <TrashIcon className="h-5 w-5" />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                    {expandedRows.includes(order.id) ? (
                                        <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                                    ) : (
                                         <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                                     )}
                                </div>
                            </div>
                        </div>

                        {expandedRows.includes(order.id) && (
                            <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
                                <div className="space-y-4">
                                    <div>
                                        <h4 className="text-sm font-medium text-gray-900 mb-1">Shipping Address</h4>
                                        {order.ship_to && (
                                            <div className="text-sm text-gray-700">
                                                <p>{order.ship_to.name}</p>
                                                <p>{order.ship_to.street1}</p>
                                                {order.ship_to.street2 && <p>{order.ship_to.street2}</p>}
                                                <p>{order.ship_to.city}, {order.ship_to.state} {order.ship_to.postalCode}</p>
                                            </div>
                                        )}
                                    </div>

                                    <div>
                                        <h4 className="text-sm font-medium text-gray-900 mb-1">Order Details</h4>
                                        <div className="grid grid-cols-2 gap-2 text-sm">
                                            <div>
                                                <p className="text-gray-500">Order Total:</p>
                                                <p className="font-medium">${parseFloat(order.order_total).toFixed(2)}</p>
                                            </div>
                                            <div>
                                                <p className="text-gray-500">Payment Method:</p>
                                                <p>{order.payment_method || 'Not specified'}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <h4 className="text-sm font-medium text-gray-900 mb-1">Items</h4>
                                        <div className="space-y-2">
                                            {order.items && order.items.map((item, idx) => (
                                                <div key={idx} className="flex justify-between text-sm">
                                                    <span className="text-gray-700 truncate max-w-[200px]">{item.name || item.sku || 'No SKU'}</span>
                                                    <div className="text-right">
                                                        <span className="font-medium">{item.quantity}x</span>
                                                        <span className="ml-1">${parseFloat(item.unit_price).toFixed(2)}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                ))
            ) : (
                 <div className="bg-white p-4 text-center text-gray-500 rounded-lg shadow">
                     No orders found.
                 </div>
             )}
        </div>
    );
};

export default MobileOrdersList;
